import { useEffect, useState } from 'react';

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

// jsdom (and any non-browser host) may not expose matchMedia; treat a
// missing implementation as "not mobile" rather than throwing during render.
const matchesMobile = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;

  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
};

export const useMobileScreen = () => {
  // Seeded from matchMedia in a lazy initializer, NOT defaulted to false —
  // the value has to be correct on the *first* render. `Modal` branches on it
  // to pick Dialog (desktop) vs Drawer (mobile), and the modal host mounts a
  // fresh Modal per state transition, so a false first render mounted a full
  // Radix Dialog — portal, focus trap, body scroll-lock — that the effect then
  // tore down one render later. On mobile that painted a white dialog frame
  // before the drawer slid up (SMB-1282).
  const [isMobileScreen, setIsMobileScreen] = useState(matchesMobile);

  // The effect only tracks *subsequent* changes (rotation, resize). Those are
  // inherently post-paint, so a passive effect is the right hook here — mount
  // is already handled by the initializer above.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileScreen(e.matches);
    };

    // Re-sync on mount as well: the viewport can change between the initial
    // render and the listener being attached.
    handleResize(mediaQuery);

    mediaQuery.addEventListener('change', handleResize);

    return () => {
      mediaQuery.removeEventListener('change', handleResize);
    };
  }, []);

  return { isMobileScreen };
};
